import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { DEFAULT_TIMEZONE } from '@commons/utils/timezone';
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { SingleDatePicker } from '@commons/DatePickers/SingleDatePicker';
import TimePicker from '@commons/TimePicker';

import { Selectors } from './styledComponents';

const TIME_PICKER_WIDTH = 180;

const NO_OP = () => {};

/**
 * Header fields of an order: its store, its external client, when it is delivered and on which
 * channel. They are editable on a draft and read-only from `A venir` onwards, where the very same
 * fields are simply disabled — except the two delivery ones, which the `Modifier la date de
 * livraison` action re-enables through `isDeliveryEditable` (COR-1771).
 *
 * The channel is not always part of the header: a central kitchen does not distinguish channels, and
 * neither does a mono-channel platform — hence a caller-driven `isChannelDisplayed`.
 */
const OrderHeaderSelectors = (props) => {
  const {
    storeName,
    stores,
    selectedStore,
    onSelectStore,
    externalClients,
    selectedExternalClient,
    onSelectExternalClient,
    deliveryDate,
    earliestDeliveryDate,
    onChangeDeliveryDate,
    deliveryTime,
    onChangeDeliveryTime,
    isChannelDisplayed,
    channels,
    selectedChannel,
    onSelectChannel,
    isReadOnly,
    isDeliveryEditable,
  } = props;

  const isDeliveryReadOnly = isReadOnly && !isDeliveryEditable;

  return (
    <Selectors>
      <Dropdown
        customStyle={{ position: 'inherit' }}
        iconSrc={
          selectedStore
            ? '/images/inpulse/store-black-small.svg'
            : '/images/inpulse/store-dmgrey-small.svg'
        }
        isDisabled={isReadOnly}
        items={stores}
        placeholder={storeName}
        selectedItem={selectedStore}
        isRequired
        isUniqueSelection
        onSelectionChange={onSelectStore}
      />
      <Dropdown
        customStyle={{ position: 'inherit' }}
        iconSrc={
          selectedExternalClient
            ? '/images/inpulse/briefcase-black-small.svg'
            : '/images/inpulse/briefcase-dmgrey-small.svg'
        }
        isDisabled={isReadOnly || !selectedStore}
        items={externalClients}
        placeholder={i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CLIENT')}
        selectedItem={selectedExternalClient}
        isUniqueSelection
        onSelectionChange={onSelectExternalClient}
      />
      <SingleDatePicker
        customStyle={{ position: 'inherit' }}
        date={deliveryDate}
        disabled={isDeliveryReadOnly}
        maxPastDate={earliestDeliveryDate}
        placeholder={i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_DATE')}
        timezone={selectedStore?.timezone || DEFAULT_TIMEZONE}
        onDateChange={onChangeDeliveryDate}
      />
      <TimePicker
        customStyle={{ position: 'inherit', width: TIME_PICKER_WIDTH }}
        disabled={isDeliveryReadOnly}
        placeholder={i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_TIME')}
        value={deliveryTime}
        onChange={onChangeDeliveryTime}
      />
      {isChannelDisplayed && (
        <Dropdown
          customStyle={{ position: 'inherit' }}
          iconSrc={'/images/inpulse/truck-black.svg'}
          isDisabled={isReadOnly}
          items={channels}
          placeholder={i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CHANNEL')}
          selectedItem={selectedChannel}
          isRequired
          isUniqueSelection
          onSelectionChange={onSelectChannel}
        />
      )}
    </Selectors>
  );
};

OrderHeaderSelectors.propTypes = {
  storeName: PropTypes.string.isRequired,
  stores: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedStore: PropTypes.object,
  onSelectStore: PropTypes.func,
  externalClients: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedExternalClient: PropTypes.object,
  onSelectExternalClient: PropTypes.func,
  deliveryDate: PropTypes.object,
  earliestDeliveryDate: PropTypes.object,
  onChangeDeliveryDate: PropTypes.func,
  deliveryTime: PropTypes.object,
  onChangeDeliveryTime: PropTypes.func,
  isChannelDisplayed: PropTypes.bool.isRequired,
  channels: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedChannel: PropTypes.object,
  onSelectChannel: PropTypes.func,
  isReadOnly: PropTypes.bool.isRequired,
  isDeliveryEditable: PropTypes.bool,
};

// Handlers are no-ops on a fully read-only header; the two delivery ones only come alive when a
// delivery edition re-enables their fields.
OrderHeaderSelectors.defaultProps = {
  selectedStore: null,
  onSelectStore: NO_OP,
  selectedExternalClient: null,
  onSelectExternalClient: NO_OP,
  deliveryDate: null,
  earliestDeliveryDate: null,
  onChangeDeliveryDate: NO_OP,
  deliveryTime: null,
  onChangeDeliveryTime: NO_OP,
  selectedChannel: null,
  onSelectChannel: NO_OP,
  isDeliveryEditable: false,
};

export default OrderHeaderSelectors;
